import { useLocation, useNavigate, useSearchParams, type To } from "react-router";

/* Dialoge über der aktuellen Seite hängen an einem Suchparameter (zum Beispiel ?neu=1). So öffnet jeder Link denselben Dialog,
   der Zurück-Knopf schließt ihn wieder, und die Module brauchen einander nicht zu kennen. Den Dialog selbst liefert ein Modul
   als Überlagerung (Modul.ueberlagerungen), die Shell legt sie über jede Seite. */

/** Neue Aufgabe: Den Dialog liefert das Modul aufgaben. */
export const NEUE_AUFGABE = "neu";
/** Eine Aufgabe öffnen (?aufgabe=<id>): derselbe Dialog aus dem Modul aufgaben (AUFGABEN-STUFE2.md, Abschnitt 6.1). */
export const AUFGABE = "aufgabe";
/** Mitteilungen als Leiste rechts über der Seite (?mitteilungen=1, KOPFZEILE.md, Abschnitt 3.4). Kommt aus der Shell. */
export const MITTEILUNGEN = "mitteilungen";

/** Vorbelegung für „Neue Aufgabe“: Wo (Sprint oder Backlog), offen ohne Person, als Unteraufgabe von, in einem Vorhaben. */
export interface NeueAufgabeVorgabe {
  wo?: "sprint" | "backlog" | undefined;
  person?: "offen" | undefined;
  eltern?: string | undefined;
  vorhaben?: string | undefined;
}

const VORGABE_SCHLUESSEL = ["wo", "person", "eltern", "vorhaben"] as const;
const DIALOGE = [NEUE_AUFGABE, AUFGABE, MITTEILUNGEN] as const;

/** Merker im Verlauf: Diesen Dialog hat ein Link in der App geöffnet. Dann schließt ihn ein Schritt zurück. */
export const DIALOG_GEOEFFNET = { dialog: true } as const;

/** Ziel eines Links, der den Dialog über der aktuellen Seite öffnet. Bestehende Suchparameter der Seite bleiben. */
export function dialogZiel(name: string, aktuelleSuche: string, vorgabe: NeueAufgabeVorgabe = {}, wert = "1"): To {
  const p = new URLSearchParams(aktuelleSuche);
  // Es ist immer nur ein Dialog offen
  for (const k of [...VORGABE_SCHLUESSEL, ...DIALOGE]) p.delete(k);
  p.set(name, wert);
  for (const k of VORGABE_SCHLUESSEL) {
    const wert = vorgabe[k];
    if (wert) p.set(k, wert);
  }
  return { search: `?${p.toString()}` };
}

/** Ziel und Verlaufsmerker für einen Link, der einen Dialog öffnet. */
export function useDialogLink(name: string, vorgabe?: NeueAufgabeVorgabe) {
  const ort = useLocation();
  return { to: dialogZiel(name, ort.search, vorgabe), state: DIALOG_GEOEFFNET };
}

/** Ziel einer Mitteilung (BAUPLAN-PAKETE.md, Abschnitt 4.4): Links auf eine Aufgabe (/aufgaben/<id>, bei Kommentaren mit #k-<id>)
 *  öffnen den Dialog über der aktuellen Seite, alle anderen führen zur Seite. Ohne Link: null. */
export function mitteilungZiel(link: string | null, aktuelleSuche: string): { to: To; dialog: boolean } | null {
  if (!link) return null;
  const treffer = /^\/aufgaben\/([^/?#]+)(#[\w-]*)?$/.exec(link);
  if (treffer?.[1]) {
    const ziel = dialogZiel(AUFGABE, aktuelleSuche, {}, decodeURIComponent(treffer[1])) as { search: string };
    return { to: treffer[2] ? { ...ziel, hash: treffer[2] } : ziel, dialog: true };
  }
  return { to: link, dialog: false };
}

/** Links auf Aufgaben in Listen: öffnen den Dialog über der aktuellen Seite. Liefert eine Funktion je id. */
export function useAufgabeLink() {
  const ort = useLocation();
  return (id: string) => ({ to: dialogZiel(AUFGABE, ort.search, {}, id), state: DIALOG_GEOEFFNET });
}

/** Ist der Dialog offen, mit welcher Vorgabe, und wie schließt man ihn? */
export function useDialog(name: string) {
  const [params] = useSearchParams();
  const ort = useLocation();
  const navigate = useNavigate();
  const offen = params.has(name);
  const wert = params.get(name);
  const vorgabe: NeueAufgabeVorgabe = {
    wo: params.get("wo") === "backlog" ? "backlog" : params.get("wo") === "sprint" ? "sprint" : undefined,
    person: params.get("person") === "offen" ? "offen" : undefined,
    eltern: params.get("eltern") ?? undefined,
    vorhaben: params.get("vorhaben") ?? undefined,
  };

  function schliessen() {
    if ((ort.state as { dialog?: boolean } | null)?.dialog) {
      navigate(-1);
      return;
    }
    // Direkt aufgerufen (Lesezeichen, /neu, Mitteilung): Parameter entfernen, ohne einen Schritt im Verlauf
    const p = new URLSearchParams(params);
    for (const k of [...DIALOGE, ...VORGABE_SCHLUESSEL]) p.delete(k);
    navigate({ pathname: ort.pathname, search: p.toString() ? `?${p.toString()}` : "" }, { replace: true });
  }

  /** Im selben Dialog weiter, zum Beispiel zur Unteraufgabe oder zur eben angelegten Aufgabe. Ersetzt den Eintrag im Verlauf,
   *  damit Schließen den ganzen Dialog schließt. */
  function wechseln(ziel: string, id: string) {
    navigate({ pathname: ort.pathname, search: (dialogZiel(ziel, ort.search, {}, id) as { search: string }).search, hash: "" }, { replace: true, state: ort.state });
  }

  return { offen, wert, vorgabe, schliessen, wechseln };
}
